import { Head, router } from "@inertiajs/react"
import { Heart, Beaker, CheckSquare, Users } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { cuppingsPath, newCuppingPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Sesiones de catación",
    href: cuppingsPath(),
  },
  {
    title: "Seleccionar Formato",
    href: "/tastings/select-format",
  },
]

interface Feature {
  icon: any
  text: string
}

interface FormatOption {
  value: string
  title: string
  description: string
  icon: any
  color: string
  iconColor: string
  hoverColor: string
  features: Feature[]
}

const formatsByProduct: Record<string, FormatOption[]> = {
  coffee: [
    {
      value: "simplified",
      title: "Aficionado",
      description:
        "Perfecto para preparadores caseros y entusiastas. Evaluaciones simplificadas, recomendaciones personalizadas y descubrimiento comunitario.",
      icon: Heart,
      color: "bg-primary/10",
      iconColor: "text-primary",
      hoverColor: "hover:border-primary",
      features: [
        { icon: Heart, text: "Calificaciones intuitivas con deslizadores" },
        { icon: Heart, text: "Personalización del método de preparación" },
        { icon: Users, text: "Recomendaciones de la comunidad" },
        { icon: Heart, text: "Guardar favoritos y colecciones" },
      ],
    },
    {
      value: "professional",
      title: "Profesional",
      description:
        "Diseñado para baristas, tostadores, productores y Q-graders. Cumplimiento total de protocolos SCA/CVA con matrices de puntuación detalladas.",
      icon: Beaker,
      color: "bg-primary/10",
      iconColor: "text-primary",
      hoverColor: "hover:border-primary",
      features: [
        { icon: CheckSquare, text: "Puntuación de precisión de 10 puntos" },
        { icon: CheckSquare, text: "Mapeo interactivo de rueda de sabores" },
        {
          icon: CheckSquare,
          text: "Informes de lotes y colaboración en equipo",
        },
        { icon: CheckSquare, text: "Exportación PDF y CSV" },
      ],
    },
  ],
}

interface Props {
  product_type: string
}

export default function SelectFormat({ product_type }: Props) {
  const formats = formatsByProduct[product_type] || []

  const handleFormatSelect = (format: string) => {
    router.get(
      `${newCuppingPath()}?product_type=${product_type}&cupping_format=${format}`,
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Elige Tu Experiencia" />

      <div className="flex h-full flex-1 flex-col gap-8 p-6">
        {/* Header */}
        <div className="mx-auto max-w-5xl text-center">
          <h1 className="mb-3 font-serif text-4xl font-bold tracking-tight">
            Elige Tu Experiencia
          </h1>
          <p className="text-muted-foreground text-lg">
            Selecciona el modo que mejor se adapte a tus necesidades. Puedes
            cambiar en cualquier momento.
          </p>
        </div>

        {/* Format Cards */}
        <div className="mx-auto grid w-full max-w-5xl gap-6 md:grid-cols-2">
          {formats.map((format) => {
            const Icon = format.icon
            return (
              <Card
                key={format.value}
                className="group hover:border-primary/50 h-full cursor-pointer transition-all hover:shadow-lg"
                onClick={() => handleFormatSelect(format.value)}
              >
                <CardHeader className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`rounded-2xl ${format.color} w-fit p-3`}>
                      <Icon className={`h-6 w-6 ${format.iconColor}`} />
                    </div>
                    <CardTitle className="font-serif text-2xl">
                      {format.title}
                    </CardTitle>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Modo Descubrimiento
                  </p>
                  <CardDescription className="text-base leading-relaxed">
                    {format.description}
                  </CardDescription>
                  <ul className="space-y-3 pt-2">
                    {format.features.map((feature, index) => {
                      const FeatureIcon = feature.icon
                      return (
                        <li
                          key={index}
                          className="flex items-start gap-3 text-sm"
                        >
                          <FeatureIcon className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                          <span>{feature.text}</span>
                        </li>
                      )
                    })}
                  </ul>
                </CardHeader>
              </Card>
            )
          })}
        </div>
      </div>
    </AppLayout>
  )
}
